import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { VoiceProductCreateResult } from "../../interfaces/VoiceProductCreateObject";
import type { VoiceCreationFailure } from "../../services/thunks/VoiceProductCreateThunks";
import {
  abandonVoiceProduct,
  prepareVoiceProduct,
  publishPreparedVoiceProduct,
  answerVoiceQuestions,
  loadSavedVoiceDrafts,
  resumeVoiceDraft,
  discardSavedVoiceDraft,
} from "../../services/thunks/VoiceProductCreateThunks";
import {
  initializeFromVoiceDraft,
  loadProductForEdit,
  resetToInitialState,
} from "./CreateProductDb";
import { resetAllState } from "./resetAllState";

interface VoiceProductCreateState {
  saved: {
    status: "idle" | "loading" | "success" | "error";
    drafts: VoiceProductCreateResult[];
    error?: VoiceCreationFailure;
    requestId?: string;
  };
  completion: {
    status: "idle" | "loading" | "error";
    requestId?: string;
    error?: VoiceCreationFailure;
  };
  analysis: {
    status: "idle" | "loading" | "success" | "error";
    requestId?: string;
    result?: VoiceProductCreateResult;
    error?: VoiceCreationFailure;
  };
  publication: {
    status: "idle" | "publishing" | "uncertain" | "conflict";
    requestId?: string;
    creationId?: string;
    voiceRequestId?: string;
    payload?: unknown;
    error?: VoiceCreationFailure;
  };
}

const initialState = (): VoiceProductCreateState => ({
  analysis: { status: "idle" },
  publication: { status: "idle" },
  saved: { status: "idle", drafts: [] },
  completion: { status: "idle" },
});
const slice = createSlice({
  name: "voiceProductCreate",
  initialState: initialState(),
  reducers: {
    resetVoiceAnalysis(state) {
      state.analysis = { status: "idle" };
      state.completion = { status: "idle" };
    },
    /** Leave a closed/conflicting publish behind so a fresh draft can be published. */
    resetVoicePublication(state) {
      state.publication = { status: "idle" };
    },
    publicationPrepared(
      state,
      action: PayloadAction<{
        requestId: string;
        creationId: string;
        voiceRequestId?: string;
        payload: unknown;
      }>,
    ) {
      if (state.publication.requestId !== action.payload.requestId) return;
      state.publication.creationId = action.payload.creationId;
      state.publication.voiceRequestId = action.payload.voiceRequestId;
      state.publication.payload = action.payload.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadSavedVoiceDrafts.pending, (state, action) => {
        state.saved.status = "loading";
        state.saved.requestId = action.meta.requestId;
      })
      .addCase(loadSavedVoiceDrafts.fulfilled, (state, action) => {
        if (state.saved.requestId === action.meta.requestId)
          state.saved = { status: "success", drafts: action.payload };
      })
      .addCase(loadSavedVoiceDrafts.rejected, (state, action) => {
        if (state.saved.requestId === action.meta.requestId)
          state.saved = { status: "error", drafts: [], error: action.payload };
      })
      .addCase(discardSavedVoiceDraft.fulfilled, (state, action) => {
        state.saved.drafts = state.saved.drafts.filter(
          (draft) => draft.requestId !== action.payload,
        );
      })
      .addCase(resumeVoiceDraft.pending, (state, action) => {
        state.completion = {
          status: "loading",
          requestId: action.meta.requestId,
        };
      })
      .addCase(resumeVoiceDraft.fulfilled, (state, action) => {
        if (state.completion.requestId !== action.meta.requestId) return;
        state.analysis = { status: "success", result: action.payload };
        state.completion = { status: "idle" };
      })
      .addCase(resumeVoiceDraft.rejected, (state, action) => {
        if (state.completion.requestId === action.meta.requestId)
          state.completion = { status: "error", error: action.payload };
      })
      .addCase(answerVoiceQuestions.pending, (state, action) => {
        state.completion = {
          status: "loading",
          requestId: action.meta.requestId,
        };
      })
      .addCase(answerVoiceQuestions.fulfilled, (state, action) => {
        if (state.completion.requestId !== action.meta.requestId) return;
        state.analysis = { status: "success", result: action.payload };
        state.completion = { status: "idle" };
      })
      .addCase(answerVoiceQuestions.rejected, (state, action) => {
        if (state.completion.requestId === action.meta.requestId)
          state.completion = { status: "error", error: action.payload };
      })
      .addCase(prepareVoiceProduct.pending, (state, action) => {
        state.analysis = {
          status: "loading",
          requestId: action.meta.requestId,
        };
      })
      .addCase(prepareVoiceProduct.fulfilled, (state, action) => {
        if (state.analysis.requestId === action.meta.requestId)
          state.analysis = { status: "success", result: action.payload };
      })
      .addCase(prepareVoiceProduct.rejected, (state, action) => {
        if (state.analysis.requestId === action.meta.requestId)
          state.analysis = {
            status: "error",
            error: action.payload ?? {
              message:
                "Preparation stopped. Try again with the same photos and recording.",
              retryable: true,
            },
          };
      })
      .addCase(publishPreparedVoiceProduct.pending, (state, action) => {
        state.publication.status = "publishing";
        state.publication.requestId = action.meta.requestId;
      })
      .addCase(publishPreparedVoiceProduct.fulfilled, (state, action) => {
        if (state.publication.requestId === action.meta.requestId)
          state.publication = { status: "idle" };
      })
      .addCase(publishPreparedVoiceProduct.rejected, (state, action) => {
        if (state.publication.requestId !== action.meta.requestId) return;
        const error: VoiceCreationFailure = action.payload ?? {
          message: "Could not confirm publication.",
          disposition: "uncertain",
        };
        state.publication =
          error.disposition === "rejected"
            ? { status: "idle", error }
            : {
                ...state.publication,
                status:
                  error.disposition === "conflict" ? "conflict" : "uncertain",
                error,
              };
      })
      .addCase(abandonVoiceProduct.fulfilled, initialState)
      .addCase(initializeFromVoiceDraft, (state) => {
        state.publication = { status: "idle" };
      })
      .addCase(loadProductForEdit, initialState)
      .addCase(resetToInitialState, initialState)
      .addCase(resetAllState, initialState);
  },
});

export const { resetVoiceAnalysis, resetVoicePublication, publicationPrepared } =
  slice.actions;
export default slice.reducer;
